import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  AuthService,
  CardTokenService,
  config,
  EXPRESS_CHECKOUT_PROVIDERS,
  ExpressCheckoutProvider,
  ExpressCheckoutProviderOptions,
  ExpressCheckoutResult,
  LANGUAGE,
  LoggingService,
  MemberQuery,
  ProviderName,
} from '@fgb/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { defaultExpressCheckoutComponents } from '../express-checkout-components';

/**
 * Resolve the wallet provider sub-id from the customer's device: Apple Pay on Apple mobile/tablet
 * (iPhone / iPad / iPod), Google Pay on Android. Returns undefined on desktop (including desktop
 * Mac), so no device wallet sub-id is sent. Passed to init as `providerSubId`. Standalone (not a
 * method) so the container can reuse it for its init pass without subclassing the base.
 */
export function resolveDeviceProviderSubId(): ProviderName | undefined {
  const ua = navigator?.userAgent ?? '';

  // iPadOS Safari reports as "Macintosh"; distinguish a touch iPad from a desktop Mac.
  const isIpadOs = /Macintosh/i.test(ua) && (navigator?.maxTouchPoints ?? 0) > 1;
  const isAppleMobile = /iPhone|iPad|iPod/i.test(ua) || isIpadOs;
  if (isAppleMobile) {
    return ProviderName.Apple;
  }

  if (/Android/i.test(ua)) {
    return ProviderName.Google;
  }

  // Desktop (or unrecognised) — send no device wallet sub-id.
  return undefined;
}

@Component({
  selector: 'fgbcl-express-checkout-providers',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './express-checkout-providers.component.html',
  styleUrl: './express-checkout-providers.component.scss',
  providers: [
    {
      provide: EXPRESS_CHECKOUT_PROVIDERS,
      useValue: defaultExpressCheckoutComponents,
    },
  ],
})
export class ExpressCheckoutProvidersComponent implements OnInit {
  @Input() providers: ProviderName[] = [ProviderName.FreedomPay, ProviderName.Paze, ProviderName.PayPal];
  @Input() successUrl = '/payments';
  @Input() failureUrl = '/payments';
  /** Re-emitted when any provider finishes (success or failure). */
  @Output() result = new EventEmitter<ExpressCheckoutResult>();

  /** Re-emitted when any provider reports the customer is ineligible / cancelled. */
  @Output() incomplete = new EventEmitter<ExpressCheckoutResult>();

  private logService = inject(LoggingService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild('providerHost', { read: ViewContainerRef, static: true }) providerHost: ViewContainerRef;

  private componentRefs: ComponentRef<ExpressCheckoutProvider>[] = [];
  private subscriptions = new Subscription();
  private providerComponents = inject(EXPRESS_CHECKOUT_PROVIDERS);
  private memberQuery = inject(MemberQuery);
  private languages = inject(LANGUAGE);
  private cardTokenService = inject(CardTokenService);
  private authService = inject(AuthService);

  /**
   * Whether the providers are still initialising. Each reveals itself with its own `ready` flag as
   * its SDK settles, so without a container-level gate the buttons appear one at a time. The row
   * stays hidden until they have all finished, so it arrives as a single unit.
   */
  initialising = true;

  /** Caps the wait, so one provider that never settles cannot hold the others back forever. */
  private static readonly READY_TIMEOUT_MS = 5000;
  private static readonly READY_POLL_MS = 50;

  ngOnInit(): void {
    this.clearProviders();

    if (!this.providerHost) {
      this.logService.error('ExpressCheckout: provider host not ready');
      return;
    }

    this.initializeProviders();
  }

  async initializeProviders(): Promise<void> {
    // Fetch every provider's init in parallel; allSettled so one failing does not reject the rest.
    const entries = this.providerEntries;

    let emailAddress = '';
    let memberLcid: string | undefined = config.defaultLcid;
    let memberId: string | undefined;

    if (this.authService.isAuthenticated()) {
      const memberDetails = await firstValueFrom(this.memberQuery.selectMemberDetails());
      const user = this.memberQuery.getValue().user;
      emailAddress = memberDetails?.EmailAddress1 ?? '';
      memberLcid = user?.lcid;
      memberId = user?.memberId;
    }

    // Currency from the member's language, falling back to the app default language's currency.
    const currencyCode = this.languages.find((lang) => lang.lcid === memberLcid)?.currencyCode ?? this.languages[0]?.currencyCode;

    entries.forEach(([providerId, options]) => {
      if (!options?.component) {
        this.logService.error(`ExpressCheckout: no component registered for provider '${ProviderName[providerId]}'`);
        return;
      }

      try {
        const componentRef = this.providerHost.createComponent(options.component);
        componentRef.setInput('context', {
          mode: 'card-token',
          emailAddress,
          memberId,
          amount: 0,
          currencyCode,
          successUrl: this.successUrl,
          failureUrl: this.failureUrl,
        });
        this.subscriptions.add(
          componentRef.instance.result.subscribe((res) => {
            if (res.success) {
              this.cardTokenService.fetchCardTokens(true).subscribe();
              this.cardTokenService.fetchDefaultCardToken().subscribe();
            }
            this.result.emit(res);
          })
        );
        this.subscriptions.add(componentRef.instance.incomplete.subscribe((res) => this.incomplete.emit(res)));
        this.componentRefs.push(componentRef);
      } catch (error) {
        // One provider failing must not prevent the others from rendering.
        this.logService.error(`ExpressCheckout: failed to create provider '${ProviderName[providerId]}'`, error);
      }
    });

    await this.awaitProvidersReady();
  }

  /**
   * Hold the row hidden until every provider has settled, so the buttons appear together rather
   * than popping in one at a time as each SDK returns.
   *
   * A provider counts as settled when it is ready and no longer loading: Paze and FreedomPay set
   * `ready` when their SDK resolves but keep a separate `loading` flag through a further
   * eligibility check, and their templates gate on both. These live on the base component rather
   * than the ExpressCheckoutProvider interface, so they are read defensively here.
   */
  private async awaitProvidersReady(): Promise<void> {
    try {
      const deadline = Date.now() + ExpressCheckoutProvidersComponent.READY_TIMEOUT_MS;

      while (Date.now() < deadline) {
        const allSettled = this.componentRefs.every((componentRef) => {
          const provider = componentRef.instance as { ready?: boolean; loading?: boolean };
          return provider.ready && !provider.loading;
        });
        if (allSettled) {
          break;
        }

        // Flush each tick: a provider that sets `ready` after an await is outside any change
        // detection pass, and PayPal has no detectChanges of its own.
        this.cdr.detectChanges();
        await new Promise((resolve) => setTimeout(resolve, ExpressCheckoutProvidersComponent.READY_POLL_MS));
      }
    } finally {
      // In a finally so the row is revealed even if the wait throws: leaving it hidden would lose
      // express checkout entirely, which is worse than revealing the buttons unevenly.
      this.initialising = false;
      this.cdr.detectChanges();
    }
  }

  /** Tear down all rendered provider components and their subscriptions. */
  private clearProviders(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.componentRefs.forEach((ref) => ref.destroy());
    this.componentRefs = [];
    this.providerHost?.clear();
  }

  /** Whether at least one enabled provider is registered and rendered. */
  get hasProviders(): boolean {
    return this.providers.length > 0;
  }

  /**
   * The providers to render: those the app enabled (EXPRESS_CHECKOUT_ENABLED), in that order, that
   * are also registered with a component (EXPRESS_CHECKOUT_PROVIDERS). Enabled-but-unregistered
   * providers are skipped so a config typo can't crash the container. FreedomPay (Google/Apple Pay
   * via HPC) is mobile-only, so it's excluded entirely on desktop — never instantiated, not just hidden.
   */
  private get providerEntries(): [ProviderName, ExpressCheckoutProviderOptions][] {
    const isMobileDevice = !!resolveDeviceProviderSubId();

    return this.providers
      .filter((providerId) => providerId !== ProviderName.FreedomPay || isMobileDevice)
      .map((providerId): [ProviderName, ExpressCheckoutProviderOptions | undefined] => [
        providerId,
        this.providerComponents[providerId],
      ])
      .filter((entry): entry is [ProviderName, ExpressCheckoutProviderOptions] => !!entry[1]);
  }
}
